import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Testing socket.io apps",
  "description": "",
  "published": "2012-11-30T00:00:00.000Z",
  "redirect_from": ["/blog/testing-socket-io-apps/swizec/5625"],
  "categories": "Client, JavaScript, Programming, Server, Uncategorized, WebSocket",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/11/nyan-cat-testing2.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/11/nyan-cat-testing2.png",
          "alt": "Nyan cat testing",
          "title": "Nyan cat testing"
        }}></img></a></p>
    <p>{`Socket.io is probably one of the coolest things to come out of the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`JavaScript`}</a>{` world in recent years. Finally! Something that lets `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Web_developer",
        "title": "Web developer"
      }}>{`web developers`}</a>{` create real-time apps without the fuss of thinking about websockets and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Push_technology",
        "title": "Push technology"
      }}>{`long polling`}</a>{` and all the other hacks that need to be used.`}</p>
    <p>{`The idea is pretty simple, the server can emit an event and the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Client_%28computing%29",
        "title": "Client (computing)"
      }}>{`client`}</a>{` will pick it up. The converse also happens. `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Callback_%28computer_programming%29",
        "title": "Callback (computer programming)"
      }}>{`Callbacks`}</a>{` through the server-client barrier works as well. Socket.io takes care of deciding which of the real-time hacks should be used to make the magic happen.`}</p>
    <p>{`Thing is, the interplay of client and server makes socket.io apps a bit difficult to test.`}</p>
    <p>{`A good way I've found is the combination of `}<a parentName="p" {...{
        "href": "http://visionmedia.github.com/mocha/"
      }}>{`Mocha`}</a>{`, `}<a parentName="p" {...{
        "href": "http://chaijs.com/"
      }}>{`Chai`}</a>{` and `}<a parentName="p" {...{
        "href": "https://github.com/LearnBoost/socket.io-client"
      }}>{`socket.io-client`}</a>{`.`}</p>
    <h2 {...{
      "id": "first-something-to-test"
    }}>{`First, something to test`}</h2>
    <p>{`Let's take for example a very simple echo server. I used `}<a parentName="p" {...{
        "href": "http://expressjs.com/"
      }}>{`Express`}</a>{` to make things easier to play with in the Chrome console. Here's the relevant part of `}<em parentName="p">{`app.js`}</em>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var server = (exports.server = http
  .createServer(app)
  .listen(app.get("port"), function () {
    console.log("Express server listening on port " + app.get("port"));
  }));

var io = require("socket.io").listen(server);
io.set("log level", 0);
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// the important parts of echo server
io.sockets.on("connection", function (socket) {
  socket.on("echo", function (msg, callback) {
    callback = callback || function () {};

    socket.emit("echo", msg);

    callback(null, "Done.");
  });
});
`}</code></pre>
    <p>{`After not forgetting to load `}<em parentName="p">{`/socket.io/socket.io.js`}</em>{` into the index page, I can now run the server, point my browser to `}<em parentName="p"><a parentName="em" {...{
          "href": "http://localhost:3000"
        }}>{`http://localhost:3000`}</a></em>{` and play around in the console like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-log"
      }}>{`> var socket = io.connect("http://localhost:3000")
undefined
> socket.on("echo", function (msg) { console.log(msg); })
SocketNamespace
> socket.emit("echo", "Hello World")
SocketNamespace
Hello World
`}</code></pre>
    <h2 {...{
      "id": "automating-the-test"
    }}>{`Automating the test`}</h2>
    <p>{`Typing commands into a console, even clicking around a webpage is a rather arduous and boring process. The easiest way I've found to automate this is using Mocha and socket.io-client.`}</p>
    <p>{`First thing we're going to need is requiring everything and making sure the socket.io server is running.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var chai = require('chai'),
    mocha = require('mocha'),
    should = chai.should();

var io = require('socket.io-client');

describe("echo", function () {

    var server,
        options ={
            transports: ['websocket'],
            'force new connection': true
        };

    beforeEach(function (done) {
        // start the server
        server = require('../app').server;

        done();
    });
`}</code></pre>
    <p>{`See, simple :)`}</p>
    <p>{`Now comes the interesting part, the actual test making sure our server does in fact echo what we ask it to.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`it("echos message", function (done) {
  var client = io.connect("http://localhost:3000", options);

  client.once("connect", function () {
    client.once("echo", function (message) {
      message.should.equal("Hello World");

      client.disconnect();
      done();
    });

    client.emit("echo", "Hello World");
  });
});
`}</code></pre>
    <p>{`The idea behind this test is simple:`}</p>
    <ol>
      <li parentName="ol">{`Connect client to server`}</li>
      <li parentName="ol">{`Once there's a connection, listen for `}<em parentName="li">{`echo`}</em>{` event from the server`}</li>
      <li parentName="ol">{`Emit `}{`_`}{`echo `}{`_`}{`event to the server`}</li>
      <li parentName="ol">{`Server responds and triggers our listener`}</li>
      <li parentName="ol">{`Listener checks correctness of response`}</li>
      <li parentName="ol">{`Disconnects client`}</li>
    </ol>
    <p>{`Disconnecting clients after tests is `}<strong parentName="p"><em parentName="strong">{`very important`}</em></strong>{`. As I've discovered, not disconnecting can lead to the socket accumulating `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Observer_pattern",
        "title": "Observer pattern"
      }}>{`event listeners`}</a>{`, which in turn can fire completely different tests than what you expect. It also leads to tests that pass 70% of the time, but fail in random ways.`}</p>
    <p>{`In the end, our efforts are rewarded by a happy nyan cat.`}</p>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/11/nyan-cat-testing.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/11/nyan-cat-testing.png",
          "alt": "Nyan cat testing",
          "title": "Nyan cat testing"
        }}></img></a></p>
    <p>{`PS: you can see all the code `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/random-coding/tree/master/socket.io-testing"
      }}>{`on github`}</a>{`.`}</p>
    <p>{`Maria Ramos from `}<a parentName="p" {...{
        "href": "http://www.webhostinghub.com/support/edu"
      }}>{`Webhostinghub.com/support/edu`}</a>{` has translated this post into `}<a parentName="p" {...{
        "href": "http://www.webhostinghub.com/support/es/misc/probando-aplicaciones-de-socketio"
      }}>{`Spanish`}</a>{`.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/12977719/how-much-data-can-i-send-through-a-socket-emit"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_119_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/12977719/how-much-data-can-i-send-through-a-socket-emit"
        }}>{`How much data can I send through a socket.emit?`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/plugging-into-socket-io-the-basics/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/125930383_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/plugging-into-socket-io-the-basics/"
        }}>{`Plugging Into Socket.IO: The Basics`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/13571376/strange-issue-with-socket-io"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_94_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/13571376/strange-issue-with-socket-io"
        }}>{`Strange Issue with Socket.IO`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.daniweb.com/software-development/java/threads/437028/single-threaded-client-server-lotto-system"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/118864313_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.daniweb.com/software-development/java/threads/437028/single-threaded-client-server-lotto-system"
        }}>{`Single-Threaded Client-Server - Lotto System`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=268bdc8c-5461-496f-9bff-f71042d50894",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      